import { type GameFormParticipant } from '../form'
import { toGameFormParticipant } from './types'
import { toAddRemoveGroupParticipants } from './util'
import { type GameGetSuccess, type GameSetting, isNotEmpty } from '@terros/common'
import { useState, useMemo, type Dispatch, type SetStateAction } from 'react'

export type GameParticipantsFormState = {
  participants: GameFormParticipant[]
  setParticipants: Dispatch<SetStateAction<GameFormParticipant[]>>
  participantSettings: GameSetting
  setParticipantSettings: Dispatch<SetStateAction<GameSetting>>
}

export const useParticipantListState = (data: GameGetSuccess): GameParticipantsFormState => {
  const [participants, setParticipants] = useState<GameFormParticipant[]>(data.participants.map(toGameFormParticipant))
  const [participantSettings, setParticipantSettings] = useState(data.game.settings)

  return useMemo(
    () => ({
      participants,
      setParticipants,
      participantSettings,
      setParticipantSettings,
    }),
    [participants, setParticipants, participantSettings, setParticipantSettings]
  )
}

export const gameHasUnsavedChanges = (
  participants: GameFormParticipant[],
  settings: GameSetting,
  data: GameGetSuccess
): boolean => {
  if (participantSettingsHasChanges(settings, data)) return true

  if (settings.participantType === 'group') {
    const { usersToRemove, groupsToAdd, groupsToUpdate, groupsToRemove } = toAddRemoveGroupParticipants(
      participants.filter((p) => p.type === 'group'),
      data.participants.filter((p) => p.type === 'group')
    )

    return (
      isNotEmpty(usersToRemove) || isNotEmpty(groupsToAdd) || isNotEmpty(groupsToUpdate) || isNotEmpty(groupsToRemove)
    )
  }
  const toAdd = participants.filter((user) => !data.participants.some((p) => p.participantId === user.participantId))
  const toRemove = data.participants.filter(
    (p) => p.type === 'user' && !participants.some((user) => user.participantId === p.participantId)
  )
  return isNotEmpty(toAdd) || isNotEmpty(toRemove)
}

export const participantSettingsHasChanges = (settings: GameSetting, data: GameGetSuccess): boolean => {
  if (data.game.settings.participantType !== settings.participantType) return true
  if (settings.participantType === 'group') {
    if (data.game.settings.participantType !== 'group') return true
    if (data.game.settings.scoringPattern !== settings.scoringPattern) return true
  }
  return false
}
